import { HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { CacheService } from '../services/cache';
import { sessionScopeFromToken } from '../services/session-scope';

const CACHE_TTL_MS = 60_000;

export const cacheInterceptor: HttpInterceptorFn = (req, next) => {
  const cacheService = inject(CacheService);

  if (req.method !== 'GET') {
    // Invalidate cache entries impacted by this mutation. The list GET is
    // cached under /api/X; the mutation targets /api/X (POST) or
    // /api/X/{id} (PUT/DELETE/PATCH). Invalidate any cached key whose URL
    // is a prefix of the mutation URL — that covers both shapes without
    // having to guess the id format.
    return next(req).pipe(
      tap(event => {
        if (event instanceof HttpResponse && event.status >= 200 && event.status < 300) {
          const mutationPath = req.url.split('?')[0];
          cacheService.invalidatePrefixesOf(mutationPath);
        }
      })
    );
  }

  // Respect standard no-cache headers as well as custom header
  const cacheControl = req.headers.get('Cache-Control') || '';
  const pragma = req.headers.get('Pragma') || '';
  const bypassCache =
    req.headers.has('X-No-Cache') ||
    cacheControl.includes('no-cache') ||
    cacheControl.includes('no-store') ||
    pragma === 'no-cache' ||
    req.urlWithParams.includes('?'); // Pagination, filters and search stay fresh.

  if (bypassCache) {
    const cleanedHeaders = req.headers
      .delete('X-No-Cache')
      .delete('Cache-Control')
      .delete('Pragma');
    return next(req.clone({ headers: cleanedHeaders }));
  }

  const scope = sessionScopeFromToken(req.headers.get('Authorization')?.replace(/^Bearer /, '') ?? null);
  if (!scope || /\/auth\//.test(req.url)) return next(req);
  const cacheKey = `${scope}|${req.urlWithParams}`;
  const cached = cacheService.get(cacheKey);
  if (cached) return of(cached.clone());

  return next(req).pipe(
    tap(event => {
      if (event instanceof HttpResponse && event.status === 200) {
        cacheService.set(cacheKey, event, CACHE_TTL_MS);
      }
    })
  );
};
